// "Open items" - top-of-page triage summary for a single person.
//
// Shows everything that still needs to be taken care of:
//   • Outstanding fines (MCCS time-payment + MVC surcharge + restoration)
//   • Active warrants
//   • Upcoming court dates / payment-due dates
//
// Each line item links to the deeper section/card on the same page so a
// staffer can jump to the underlying detail with one click.
//
// The section RENDERS NOTHING when there are zero open items - for a
// fully-resolved person we don't want to take vertical space repeating
// "no open items" (the absence of the section IS that signal).
//
// Renders the full section in two parts:
//   1. Headline tile - total $ + item count + next court-date countdown.
//      Color-shifts by total amount + nearest date urgency.
//   2. Line-item list - one row per actionable item, with link/anchor.
//
// Layout responsively adapts:
//   • >= 720px wide: headline tile spans left half, list spans right.
//   • <  720px:      headline stacks above the list.

import { useMemo } from "react";
import type { RecordRow, UpcomingCourtDate } from "../types";
import type { ExternalLookup, ExternalSource } from "./ExternalSourcesPanel";
import {
  deriveOpenItems,
  type OpenItem,
  type ItemUrgency,
  type OpenItemLink,
  type OpenItemsResult,
} from "../lib/openItems";

// ─────────────────────────────────────────────────────────────────────────
// Color helpers
// ─────────────────────────────────────────────────────────────────────────

/** Tile gradient - picks a hue based on the most-urgent thing in the
 *  summary. Keeps the visual language consistent with HavocHero gradients
 *  on the same page. */
function pickHeadlineGradient(opts: {
  totalDollars: number;
  daysToNext: number | null;
  hasWarrant: boolean;
}): { bg: string; ink: string; soft: string } {
  // Drenched gradients in BOTH themes - the pastel light-mode
  // experiment washed out the urgency tiers so badly the tile lost
  // its "this is a crisis" energy.
  if (opts.hasWarrant || (opts.daysToNext !== null && opts.daysToNext <= 7)) {
    return {
      bg: "linear-gradient(155deg, #8C1F2F 0%, #5A1320 100%)",
      ink: "#fff",
      soft: "rgba(255,255,255,0.85)",
    };
  }
  if (opts.totalDollars >= 1000) {
    return {
      bg: "linear-gradient(155deg, #A85A26 0%, #6E3812 100%)",
      ink: "#fff",
      soft: "rgba(255,255,255,0.86)",
    };
  }
  if (opts.totalDollars > 0 || (opts.daysToNext !== null && opts.daysToNext <= 30)) {
    return {
      bg: "linear-gradient(155deg, #7A5C2D 0%, #4D3A19 100%)",
      ink: "#fff",
      soft: "rgba(255,255,255,0.86)",
    };
  }
  return {
    bg: "linear-gradient(155deg, #2E4258 0%, #14202E 100%)",
    ink: "#fff",
    soft: "rgba(255,255,255,0.82)",
  };
}

/** Per-row accent based on item urgency - used for the left edge stripe
 *  + the chevron color. */
function urgencyAccent(u: ItemUrgency): string {
  if (u === "high") return "var(--color-rose-hi, #f59e9e)";
  if (u === "med") return "var(--color-amber-hi, #ffd166)";
  return "var(--color-text-dim, #99a)";
}

/** Icon character for each item kind. Kept ASCII-ish so the typography
 *  matches the rest of the page (no emoji clash with Fraunces serif). */
function itemIcon(kind: OpenItem["kind"]): string {
  switch (kind) {
    case "mccs-warrant":
      return "!";
    case "mccs-fines":
      return "$";
    case "mvc-surcharge":
      return "$";
    case "mvc-restoration":
      return "$";
    case "court-date":
      return "▸";
    case "payment-due":
      return "◆";
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

interface Props {
  records: RecordRow[];
  upcomingDates: UpcomingCourtDate[];
  /** External-lookup map lifted from PersonPage's useExternalLookups.
   *  Used to read MVC surcharge / restoration totals. Self-fetching here
   *  before consolidation produced four parallel network requests for
   *  the same endpoint. */
  externalLatest: Partial<Record<ExternalSource, ExternalLookup | null>>;
  /** True until the parent's first fetch completes. While loading, the
   *  summary suppresses the "no items" empty-render (avoids a brief
   *  flash where the section says "no open items" before MVC totals
   *  arrive and add line items). */
  externalLoading: boolean;
  /** True when ANY background check (MCCS or external) is still in
   *  flight. Drives a "totals may still grow" caveat next to the
   *  headline dollar amount so junior staff don't act on an in-progress
   *  number. */
  scrapesPending: boolean;
  /** Single click dispatch for any item with a link target. PersonPage
   *  handles each variant - switching the view, toggling open-only,
   *  scrolling with sticky-header offset, flashing the destination
   *  card. Plain href anchors don't reach the right place because most
   *  targets only exist in the cards view. */
  onJump: (target: OpenItemLink) => void;
}

// Empty result we fall back to when derivation throws - keeps the
// component renderable instead of crashing the whole page if some
// record has unexpected shape.
const EMPTY_RESULT: OpenItemsResult = {
  totalDollars: 0,
  hasUnknownAmounts: false,
  itemCount: 0,
  items: [],
  nextCourtDate: null,
};

export function OpenItemsSummary({
  records,
  upcomingDates,
  externalLatest,
  externalLoading,
  scrapesPending,
  onJump,
}: Props): JSX.Element | null {
  // Wrap derivation in try/catch - if any record has unexpected shape
  // and our parser throws, we still want the rest of the page to render.
  // Better to silently degrade than to black-screen the whole person.
  const result = useMemo<OpenItemsResult>(() => {
    try {
      return deriveOpenItems({
        records,
        upcomingDates,
        externalLatest: externalLatest as Record<string, ExternalLookup | null>,
      });
    } catch (err) {
      console.error("OpenItemsSummary: deriveOpenItems threw", err);
      return EMPTY_RESULT;
    }
  }, [records, upcomingDates, externalLatest]);

  // Hide when there's literally nothing to do AND we've finished fetching
  // externals (so we don't briefly show an empty state during initial
  // load that would then re-pop with content).
  if (result.itemCount === 0 && !externalLoading) return null;

  const { totalDollars, hasUnknownAmounts, items, nextCourtDate, itemCount } = result;
  const hasWarrant = items.some((i) => i.kind === "mccs-warrant");
  const daysToNext = nextCourtDate?.daysOut ?? null;
  const urgency = pickHeadlineGradient({ totalDollars, daysToNext, hasWarrant });
  const accent = {
    bg: "var(--color-bg-card)",
    ink: "var(--color-text)",
    soft: "var(--color-text-muted)",
    line: hasWarrant || (daysToNext !== null && daysToNext <= 7) ? "#8C1F2F" : "var(--color-gold-cream)",
  };

  return (
    <section className="container station station-flush-top">
      <div
        className="card-elevated overflow-hidden"
        style={{
          background: accent.bg,
          color: accent.ink,
          border: "1px solid var(--color-line)",
          boxShadow: "var(--e-raised)",
          borderTop: `4px solid ${accent.line}`,
        }}
      >
        {/* ── Header strip ────────────────────────────────────────────── */}
        <div className="px-5 pt-5 sm:px-6 sm:pt-6 flex items-baseline justify-between gap-3 flex-wrap">
          <div
            className="eyebrow text-[11px]"
            style={{ color: accent.soft, letterSpacing: "0.18em" }}
          >
            Open items
          </div>
          <div
            className="text-[11px] uppercase"
            style={{ color: accent.soft, letterSpacing: "0.14em" }}
          >
            {itemCount} {itemCount === 1 ? "item" : "items"} need{itemCount === 1 ? "s" : ""} attention
          </div>
        </div>

        {/* ── Headline metric grid ────────────────────────────────────── */}
        <div className="grid gap-5 p-5 sm:p-6 sm:grid-cols-[1fr_1fr] sm:gap-6">
          {/* Total $ owed */}
          <div>
            <div
              className="eyebrow text-[10px]"
              style={{ color: accent.soft, letterSpacing: "0.2em" }}
            >
              Total to resolve
            </div>
            <div
              className="display mt-1 leading-none tabular"
              style={{
                fontSize: "clamp(34px, 5vw, 56px)",
                fontVariationSettings: '"opsz" 144, "SOFT" 30',
                color: accent.line,
              }}
            >
              {totalDollars > 0
                ? `$${totalDollars.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : "-"}
            </div>
            <div className="text-[12px] mt-1.5" style={{ color: accent.soft }}>
              {totalDollars > 0
                ? `across ${items.filter((i) => i.amount !== null && i.amount > 0).length} fine source${items.filter((i) => i.amount !== null && i.amount > 0).length === 1 ? "" : "s"}`
                : items.length > 0
                  ? "no fines yet"
                  : "no open items"}
              {hasUnknownAmounts && (
                <span style={{ marginLeft: 6, opacity: 0.85 }}>· some unparsed</span>
              )}
              {/* Caveat when checks are still in flight - the total
                  could grow as MVC / DOC / etc. results land. Without
                  this, a junior reviewer might quote the current number
                  to a client mid-scrape and get a surprise later. */}
              {scrapesPending && (
                <span
                  style={{
                    marginLeft: 6,
                    opacity: 0.95,
                    fontStyle: "italic",
                    fontWeight: 600,
                  }}
                >
                  · totals may still grow
                </span>
              )}
            </div>
          </div>

          {/* Next court date / payment-due */}
          <div className="sm:text-right">
            <div
              className="eyebrow text-[10px]"
              style={{ color: accent.soft, letterSpacing: "0.2em" }}
            >
              {nextCourtDate?.kind === "payment" ? "Next payment due" : "Next court date"}
            </div>
            {nextCourtDate ? (
              <>
                <div
                  className="display mt-1 leading-none tabular"
                  style={{
                    fontSize: "clamp(28px, 3.5vw, 40px)",
                    fontVariationSettings: '"opsz" 96, "SOFT" 30',
                    color: urgency.ink === "#fff" ? accent.ink : urgency.ink,
                  }}
                >
                  {daysToNextLabel(typeof nextCourtDate.daysOut === "number" ? nextCourtDate.daysOut : 0)}
                </div>
                <div className="text-[12px] mt-1.5 truncate" style={{ color: accent.soft }}>
                  {longDate(String(nextCourtDate.date ?? ""))} · {String(nextCourtDate.description ?? "").slice(0, 60)}
                </div>
              </>
            ) : (
              <>
                <div
                  className="display mt-1 leading-none"
                  style={{
                    fontSize: "clamp(22px, 2.5vw, 30px)",
                    fontVariationSettings: '"opsz" 96, "SOFT" 30',
                    color: accent.ink,
                    opacity: 0.85,
                  }}
                >
                  none on file
                </div>
                <div className="text-[12px] mt-1.5" style={{ color: accent.soft }}>
                  no upcoming appearances or due dates
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Line items ──────────────────────────────────────────────── */}
        {items.length > 0 && (
          <div
            className="border-t"
            style={{ borderColor: "rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.18)" }}
          >
            <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
              {items.map((item, idx) => (
                <OpenItemRow
                  key={`${item.kind}-${idx}`}
                  item={item}
                  accentSoft={accent.soft}
                  onJump={onJump}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Single-row component
// ─────────────────────────────────────────────────────────────────────────

function OpenItemRow({
  item,
  accentSoft,
  onJump,
}: {
  item: OpenItem;
  accentSoft: string;
  onJump: (target: OpenItemLink) => void;
}): JSX.Element {
  const handleClick = (e: React.MouseEvent): void => {
    if (!item.link) return;
    // Always intercept - every link target requires PersonPage state
    // setup (view mode, filters) before the scroll can land on a real
    // element. Plain href-jumping would land on hidden DOM.
    e.preventDefault();
    onJump(item.link);
  };

  // href is set to a "best-guess" anchor for accessibility (keyboard
  // navigation, right-click "open in new tab" - though new-tab won't
  // do the cards-view setup, that's acceptable for a fallback). The
  // onClick handler always intercepts and routes through onJump.
  const href = (() => {
    if (!item.link) return undefined;
    switch (item.link.kind) {
      case "record": return `#record-${item.link.recordId}`;
      case "mvc-source": return `#source-${item.link.source}`;
      case "open-records": return "#source-mpa";
    }
  })();

  // Whole row is clickable only when there's a link target.
  const Tag: "a" | "div" = href ? "a" : "div";

  const u = urgencyAccent(item.urgency);
  const icon = itemIcon(item.kind);

  return (
    <li>
      <Tag
        href={href}
        onClick={handleClick}
        className="group flex items-center gap-3 px-5 sm:px-6 py-3 transition-colors hover:bg-[rgba(255,255,255,0.05)]"
        style={{ color: "inherit", textDecoration: "none", cursor: href ? "pointer" : "default" }}
      >
        {/* Left: icon swatch */}
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[12px] font-bold tabular"
          style={{
            background: `color-mix(in oklab, ${u} 22%, transparent)`,
            color: u,
            border: `1px solid color-mix(in oklab, ${u} 45%, transparent)`,
            fontFamily: "var(--font-mono)",
          }}
          aria-hidden="true"
        >
          {icon}
        </span>

        {/* Middle: label + detail */}
        <div className="min-w-0 flex-1">
          <div className="text-[14px] sm:text-[15px] font-semibold truncate">{item.label}</div>
          {item.detail && (
            <div className="text-[12px] truncate" style={{ color: accentSoft }}>
              {item.detail}
            </div>
          )}
        </div>

        {/* Right: value + chevron */}
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-[14px] sm:text-[15px] font-semibold tabular"
            style={{ color: item.amount !== null ? "inherit" : accentSoft }}
          >
            {item.value}
          </span>
          {href && (
            <span
              className="text-[14px] opacity-60 group-hover:opacity-100 transition-opacity"
              aria-hidden="true"
            >
              →
            </span>
          )}
        </div>
      </Tag>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Date formatting (kept local - small enough not to share)
// ─────────────────────────────────────────────────────────────────────────

function daysToNextLabel(days: number): string {
  if (days < 0) return `${-days}d ago`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 7) return `in ${days}d`;
  if (days < 14) return "next week";
  if (days < 31) return `in ${Math.round(days / 7)}w`;
  if (days < 60) return "next month";
  if (days < 365) return `in ${Math.round(days / 30)}mo`;
  return `in ${Math.round(days / 365)}y`;
}

function longDate(iso: string): string {
  // Defensive: callers occasionally pass through DB rows where the date
  // field could be unexpected. String-coerce + bail out on anything that
  // doesn't look like an ISO date instead of throwing on .split.
  const safe = typeof iso === "string" ? iso : "";
  const parts = safe.split("-").map((n) => parseInt(n, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return safe;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
